import { useEffect, useMemo, useState } from "react";
import Icon from "./Icon";
import Skeleton from "../Skeleton";
import { CountUp, Delta, Donut, Funnel, Heatmap, RankBars, SplitBar, Sparkline, TrendChart, formatInt, formatMoney, formatPct } from "./StatsCharts";
import { WEEKDAYS, demoSummary, hourRange, peaks, summarize } from "./statsData";
import { DETAILED_STATS_SINCE, dayKey, loadStats } from "../../stats.js";
import { PRO_PRICE, PRO_TRIAL_DAYS } from "../../plans.js";
import "../../panel-stats.css";

const RANGES = [7, 30, 90];

const SERIES = [
    { key: "visitas", label: "Visitas", color: "var(--lp-st-1)" },
    { key: "pedidos", label: "Pedidos", color: "var(--lp-st-2)" }
];

// Lo que incluye la sección, para el cartel del plan Gratis.
export const STATS_FEATURES = [
    { icon: "chart", title: "Visitas y pedidos por día", text: "Con la comparación contra el período anterior." },
    { icon: "funnel", title: "Conversión", text: "De cada 100 visitas, cuántas arman un pedido y cuántas lo envían." },
    { icon: "cart", title: "Carritos abandonados", text: "Quiénes armaron un pedido y no lo mandaron." },
    { icon: "money", title: "Ticket promedio y total estimado", text: "Cuánto suman los pedidos que te llegan por WhatsApp." },
    { icon: "clock", title: "Horarios y días pico", text: "Para saber cuándo reforzar el personal o lanzar promos." },
    { icon: "star", title: "Productos estrella", text: "Los más vistos, los más agregados y los más pedidos." },
    { icon: "eye", title: "Vistos pero poco pedidos", text: "Productos que miran y no piden: quizás es el precio o la foto." },
    { icon: "grid", title: "Categorías más visitadas", text: "Qué parte de tu menú atrae más." },
    { icon: "qr", title: "QR o link", text: "Si entran escaneando el QR o por el link que compartiste." },
    { icon: "truck", title: "Retiro o envío", text: "Cómo prefieren recibir los pedidos tus clientes." },
    { icon: "whatsapp", title: "Clicks en WhatsApp", text: "Cuántos te escriben desde el botón del menú." }
];

const plural = (n, one, many) => n === 1 ? one : many;

// Frases cortas con lo más importante del período.
const insightsOf = (s, prev, heat, days) => {
    const t = s.totals;
    const out = [];
    if (t.visitas) {
        const change = prev.totals.visitas ? (t.visitas - prev.totals.visitas) / prev.totals.visitas : null;
        out.push({
            icon: "chart",
            text: <>Tu menú tuvo <strong>{formatInt(t.visitas)} {plural(t.visitas, "visita", "visitas")}</strong>
                {change === null ? "." : Math.abs(change) < 0.005 ? `, igual que los ${days} días anteriores.` :
                    <>, <strong>{formatPct(Math.abs(change))} {change > 0 ? "más" : "menos"}</strong> que los {days} días anteriores.</>}</>
        });
    }
    if (heat.cell.n > 0) {
        out.push({ icon: "clock", text: <>El momento con más visitas es el <strong>{WEEKDAYS[heat.cell.row].toLowerCase()} de {hourRange(heat.cell.hour)}</strong>.</> });
    }
    if (t.pedidos) {
        const per100 = Math.round(s.conversion * 100);
        out.push({
            icon: "funnel",
            text: per100 >= 1 ?
                <>De cada 100 visitas, <strong>{per100} {plural(per100, "termina", "terminan")} en un pedido</strong> por WhatsApp.</> :
                <>Te mandaron <strong>{formatInt(t.pedidos)} {plural(t.pedidos, "pedido", "pedidos")}</strong> por WhatsApp.</>
        });
    }
    if (s.abandoned) {
        out.push({ icon: "cart", text: <><strong>{formatInt(s.abandoned)} {plural(s.abandoned, "persona armó", "personas armaron")} un pedido</strong> y no {plural(s.abandoned, "lo envió", "lo enviaron")}.</> });
    }
    if (s.topViewed[0]) {
        out.push({ icon: "star", text: <>Lo que más miran: <strong>{s.topViewed[0].product.name}</strong>.</> });
    }
    if (t.visitasQr) {
        out.push({ icon: "qr", text: <>El <strong>{formatPct(t.visitasQr / t.visitas)}</strong> de las visitas entra escaneando tu QR.</> });
    }
    return out.slice(0, 4);
}

const productItems = (list) => list.map((r) => ({ key: r.product.id, label: r.product.name, n: r.n }));

const PRODUCT_TABS = [
    { id: "vistos", label: "Más vistos", list: "topViewed", unit: (n) => plural(n, "vista", "vistas"), empty: "Todavía nadie abrió el detalle de un producto en este período." },
    { id: "agregados", label: "Más agregados", list: "topAdded", unit: (n) => plural(n, "vez", "veces"), empty: "Todavía nadie agregó productos al pedido en este período." },
    { id: "pedidos", label: "Más pedidos", list: "topSent", unit: (n) => plural(n, "unidad", "unidades"), empty: "Todavía no hay pedidos enviados en este período." }
];

// Contenido de la sección con los datos ya calculados. Lo usa la vista real y la previa del plan Gratis.
export const StatsBoard = ({ summary: s, previous: prev, days, ordersOn, goTo }) => {

    const [productTab, setProductTab] = useState("vistos");
    const [heatMetric, setHeatMetric] = useState("visitas");
    const t = s.totals;
    const p = prev.totals;
    const heat = peaks(s.heat[heatMetric]);
    const visitsHeat = peaks(s.heat.visitas);
    const insights = insightsOf(s, prev, visitsHeat, days);
    const tab = PRODUCT_TABS.find((x) => x.id === productTab);
    const vsLabel = `los ${days} días anteriores`;

    const kpis = [
        { label: "Visitas", value: t.visitas, before: p.visitas, spark: s.byDay.map((d) => d.visitas), hint: "Personas que abrieron tu menú" },
        { label: "Pedidos enviados", value: t.pedidos, before: p.pedidos, spark: s.byDay.map((d) => d.pedidos), hint: "Mandados a tu WhatsApp" },
        { label: "Conversión", value: s.conversion, before: prev.conversion, format: formatPct, hint: "Visitas que terminan en pedido" },
        { label: "Ticket promedio", value: s.ticket, before: prev.ticket, format: formatMoney, hint: "Lo que suma cada pedido" },
        { label: "Total estimado", value: s.estimated, before: prev.estimated, format: formatMoney, hint: "En pedidos por WhatsApp" },
        { label: "Carritos abandonados", value: s.abandoned, before: prev.abandoned, invert: true, hint: "Armaron pedido y no lo enviaron" }
    ];

    return (
        <>
            {!ordersOn &&
                <div className="lp-panel-notice lp-st-notice">
                    <Icon name="cart" size={20}></Icon>
                    <span>Los pedidos por WhatsApp están apagados, así que no vas a ver datos nuevos de carritos, pedidos ni ticket. <button type="button" className="lp-text-link" onClick={() => goTo && goTo("negocio")}>Activarlos</button></span>
                </div>
            }

            <div className="lp-st-kpis">
                {kpis.map((k, i) => (
                    <section key={k.label} className="lp-panel-card lp-st-kpi" style={{ "--i": i }}>
                        <span className="lp-st-kpi-label">{k.label}</span>
                        <strong className="lp-st-kpi-value"><CountUp value={k.value} format={k.format}></CountUp></strong>
                        <div className="lp-st-kpi-foot">
                            <Delta now={k.value} before={k.before} invert={k.invert} label={vsLabel}></Delta>
                            <span className="lp-panel-muted">{k.hint}</span>
                        </div>
                        {k.spark && <Sparkline values={k.spark}></Sparkline>}
                    </section>
                ))}
            </div>

            {insights.length > 0 &&
                <section className="lp-panel-card lp-st-insights">
                    <h2>En pocas palabras</h2>
                    <ul>
                        {insights.map((x, i) => (
                            <li key={i} style={{ "--i": i }}>
                                <span className="lp-st-insight-icon" aria-hidden="true"><Icon name={x.icon} size={18}></Icon></span>
                                <span>{x.text}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            }

            <section className="lp-panel-card lp-panel-stack">
                <div className="lp-panel-card-head lp-st-wrap-head">
                    <div>
                        <h2>Visitas y pedidos por día</h2>
                        <p className="lp-panel-muted">Pasá el dedo o el mouse por el gráfico para ver cada día.</p>
                    </div>
                    <ul className="lp-st-legend lp-st-legend-row">
                        {SERIES.map((x) => <li key={x.key}><i className={"lp-st-swatch lp-st-series-" + x.key}></i><span>{x.label}</span></li>)}
                    </ul>
                </div>
                <TrendChart days={s.byDay} series={SERIES}></TrendChart>
            </section>

            <div className="lp-st-grid">
                <section className="lp-panel-card lp-panel-stack">
                    <div>
                        <h2>Del menú al pedido</h2>
                        <p className="lp-panel-muted">Cuántas visitas avanzan en cada paso.</p>
                    </div>
                    <Funnel steps={[
                        { label: "Abrieron el menú", n: t.visitas },
                        { label: "Miraron un producto", n: t.exploraron },
                        { label: "Armaron un pedido", n: t.carritos },
                        { label: "Lo enviaron por WhatsApp", n: t.pedidos }
                    ]}></Funnel>
                    {s.abandoned > 0 &&
                        <p className="lp-st-callout"><Icon name="cart" size={18}></Icon><span><strong>{formatInt(s.abandoned)} {plural(s.abandoned, "carrito quedó", "carritos quedaron")} sin enviar.</strong> Un precio claro y fotos lindas ayudan a que terminen el pedido.</span></p>
                    }
                </section>

                <section className="lp-panel-card lp-panel-stack">
                    <div className="lp-panel-card-head lp-st-wrap-head">
                        <div>
                            <h2>Horarios pico</h2>
                            <p className="lp-panel-muted">Cuanto más intenso el color, más movimiento.</p>
                        </div>
                        <div className="lp-st-seg" role="radiogroup" aria-label="Qué mostrar">
                            {SERIES.map((x) => (
                                <button key={x.key} type="button" role="radio" aria-checked={heatMetric === x.key} onClick={() => setHeatMetric(x.key)}>{x.label}</button>
                            ))}
                        </div>
                    </div>
                    <Heatmap key={heatMetric} grid={s.heat[heatMetric]} label={`${heatMetric === "visitas" ? "Visitas" : "Pedidos"} por día de la semana y hora`}></Heatmap>
                    <div className="lp-st-peaks">
                        <div><span className="lp-panel-muted">Día más fuerte</span><strong>{heat.topDay >= 0 ? WEEKDAYS[heat.topDay] : "–"}</strong></div>
                        <div><span className="lp-panel-muted">Hora más fuerte</span><strong>{heat.topHour >= 0 ? hourRange(heat.topHour) : "–"}</strong></div>
                    </div>
                </section>

                <section className="lp-panel-card lp-panel-stack">
                    <div>
                        <h2>Tus productos</h2>
                        <div className="lp-st-tabs" role="tablist" aria-label="Ranking de productos">
                            {PRODUCT_TABS.map((x) => (
                                <button key={x.id} type="button" role="tab" aria-selected={productTab === x.id} onClick={() => setProductTab(x.id)}>{x.label}</button>
                            ))}
                        </div>
                    </div>
                    <div role="tabpanel" aria-label={tab.label}>
                        <RankBars key={productTab} items={productItems(s[tab.list])} unit={tab.unit} empty={tab.empty}></RankBars>
                    </div>
                </section>

                <section className="lp-panel-card lp-panel-stack">
                    <div>
                        <h2>Los miran, pero no los piden</h2>
                        <p className="lp-panel-muted">Productos que abren seguido y casi nadie agrega. Revisá el precio, la foto o la descripción.</p>
                    </div>
                    {s.lowAdd.length ?
                        <ul className="lp-st-low">
                            {s.lowAdd.map((r, i) => (
                                <li key={r.product.id} style={{ "--i": i }}>
                                    {r.product.image ? <img src={r.product.image} alt="" loading="lazy"/> : <span className="lp-st-low-img" aria-hidden="true"><Icon name="box" size={18}></Icon></span>}
                                    <div>
                                        <strong>{r.product.name}</strong>
                                        <span className="lp-panel-muted">{formatInt(r.n)} {plural(r.n, "vista", "vistas")} · {formatInt(r.added)} {plural(r.added, "lo agregó", "lo agregaron")}</span>
                                    </div>
                                    <span className="lp-st-low-rate">{formatPct(r.rate)}</span>
                                </li>
                            ))}
                        </ul> :
                        <p className="lp-panel-muted lp-st-empty"><Icon name="check" size={18}></Icon> {s.hasViews && ordersOn ? "¡Bien! Lo que más miran, también lo piden." : "Cuando tengas más visitas y pedidos te vamos a avisar acá."}</p>
                    }
                </section>

                <section className="lp-panel-card lp-panel-stack">
                    <div>
                        <h2>Categorías más visitadas</h2>
                        <p className="lp-panel-muted">Según los productos que abren.</p>
                    </div>
                    <RankBars items={s.categories.map((c) => ({ key: c.name, label: c.name, n: c.n }))} unit={(n) => plural(n, "vista", "vistas")} empty="Todavía nadie abrió el detalle de un producto en este período."></RankBars>
                </section>

                <section className="lp-panel-card lp-panel-stack">
                    <div>
                        <h2>¿Cómo llegan a tu menú?</h2>
                        <p className="lp-panel-muted">Escaneando el QR o tocando el link que compartiste.</p>
                    </div>
                    <Donut center={formatInt(t.visitas)} caption={plural(t.visitas, "visita", "visitas")} parts={[
                        { label: "QR", n: t.visitasQr, tone: 1 },
                        { label: "Link", n: Math.max(0, t.visitas - t.visitasQr), tone: 2 }
                    ]}></Donut>
                </section>

                <section className="lp-panel-card lp-panel-stack">
                    <div>
                        <h2>Retiro o envío</h2>
                        <p className="lp-panel-muted">Lo que eligieron en los pedidos enviados.</p>
                    </div>
                    <SplitBar parts={[
                        { label: "Retiro", n: t.retiro, tone: 1 },
                        { label: "Envío", n: t.envio, tone: 2 },
                        { label: "Sin indicar", n: Math.max(0, t.pedidos - t.retiro - t.envio), tone: 3 }
                    ]}></SplitBar>
                </section>

                <section className="lp-panel-card lp-panel-stack">
                    <div>
                        <h2>Clicks en WhatsApp</h2>
                        <p className="lp-panel-muted">Toques en el botón de WhatsApp de arriba del menú, aunque no armen pedido.</p>
                    </div>
                    <div className="lp-st-big">
                        <strong><CountUp value={t.clicsWhatsapp}></CountUp></strong>
                        <Delta now={t.clicsWhatsapp} before={p.clicsWhatsapp} label={vsLabel}></Delta>
                    </div>
                    <span className="lp-panel-muted">{t.visitas ? `${formatPct(t.clicsWhatsapp / t.visitas)} de las visitas.` : ""}</span>
                </section>
            </div>
        </>
    )
}

const BoardSkeleton = () => (
    <div className="lp-st-skel" aria-hidden="true">
        <div className="lp-st-kpis">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={128} radius={16}></Skeleton>)}</div>
        <Skeleton height={300} radius={16}></Skeleton>
        <div className="lp-st-grid"><Skeleton height={280} radius={16}></Skeleton><Skeleton height={280} radius={16}></Skeleton></div>
    </div>
);

// Cartel del plan Gratis, arriba de una vista previa borrosa con datos de ejemplo.
const StatsUpsell = () => {
    const demo = useMemo(() => demoSummary(), []);
  return (
    <div className="lp-st-locked">
        <div className="lp-st-locked-preview" aria-hidden="true" {...{ inert: "" }}>
            <StatsBoard summary={demo.summary} previous={demo.previous} days={30} ordersOn></StatsBoard>
        </div>
        <section className="lp-panel-card lp-st-locked-card">
            <span className="lp-panel-badge lp-st-pro-badge">Pro</span>
            <h2>Conocé de verdad cómo usan tu menú</h2>
            <p className="lp-panel-muted">Con el plan Pro tenés todas estas estadísticas, explicadas en gráficos simples:</p>
            <ul className="lp-st-features">
                {STATS_FEATURES.map((f, i) => (
                    <li key={f.title} style={{ "--i": i }}>
                        <span className="lp-st-insight-icon" aria-hidden="true"><Icon name={f.icon} size={18}></Icon></span>
                        <span><strong>{f.title}</strong><span className="lp-panel-muted">{f.text}</span></span>
                    </li>
                ))}
            </ul>
            <a className="lp-btn lp-btn-primary lp-st-cta" href="#cuenta">Probar Pro gratis {PRO_TRIAL_DAYS} días</a>
            <p className="lp-panel-muted lp-st-cta-note">Después {PRO_PRICE} por mes. Cancelás cuando quieras.</p>
        </section>
    </div>
  )
}

// Sección Estadísticas del panel. En Gratis muestra el cartel para pasarse a Pro.
const StatsPanel = ({ business, products, goTo }) => {

    const pro = business.subscribed === true;
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        setData(null);
    }, [days]);

    useEffect(() => {
        if (!pro) return;
        let alive = true;
        setFailed(false);
        // El período elegido y el anterior, para comparar.
        Promise.all([loadStats(business.usuarioEmpresa, days), loadStats(business.usuarioEmpresa, days, days)])
            .then(([current, before]) => alive && setData({ current, before, days }))
            .catch((error) => {
                console.error(error);
                if (alive) setFailed(true);
            })
            .finally(() => alive && setRefreshing(false));
        return () => { alive = false; };
    }, [pro, business.usuarioEmpresa, days, refreshKey]);

    const summary = useMemo(() => data && summarize(data.current, products), [data, products]);
    const previous = useMemo(() => data && summarize(data.before, products), [data, products]);

    const refresh = () => {
        setRefreshing(true);
        setRefreshKey((k) => k + 1);
    }

    const since = data && dayKey(data.current[0].date) < DETAILED_STATS_SINCE;

  return (
    <div className="lp-panel-page lp-st-page">
        <div className="lp-panel-head">
            <div>
                <h1>Estadísticas {!pro && <span className="lp-panel-badge lp-st-pro-badge">Pro</span>}</h1>
                <p className="lp-panel-muted">Cómo usan tu menú tus clientes, para decidir con datos.</p>
            </div>
            {pro &&
                <div className="lp-st-controls">
                    <div className="lp-st-seg" role="radiogroup" aria-label="Período">
                        {RANGES.map((r) => (
                            <button key={r} type="button" role="radio" aria-checked={days === r} onClick={() => setDays(r)}>{r} días</button>
                        ))}
                    </div>
                    <button type="button" className={"lp-panel-icon-btn lp-panel-refresh" + (refreshing ? " lp-panel-refresh-on" : "")} onClick={refresh} disabled={refreshing} aria-label="Actualizar estadísticas" title="Actualizar">
                        <Icon name="refresh" size={18}></Icon>
                    </button>
                </div>
            }
        </div>

        {!pro ? <StatsUpsell></StatsUpsell> :
            failed ?
                <section className="lp-panel-card lp-panel-stack">
                    <p>No pudimos cargar las estadísticas. Probá de nuevo en un rato.</p>
                    <button type="button" className="lp-btn lp-btn-secondary lp-panel-btn-sm lp-panel-left" onClick={refresh}>Reintentar</button>
                </section> :
            !summary ? <BoardSkeleton></BoardSkeleton> :
                <div key={data.days} className="lp-st-board">
                    {since &&
                        <p className="lp-panel-muted lp-st-since">Carritos, horarios, origen, entrega y clicks en WhatsApp se miden desde el 30 de octubre de 2026.</p>
                    }
                    <StatsBoard summary={summary} previous={previous} days={data.days} ordersOn={business.pedidos === true} goTo={goTo}></StatsBoard>
                </div>
        }
    </div>
  )
}

export default StatsPanel
